/**
 * Utility function to replace Gutenberg block div elements with anchor tags
 * Specifically targets elements with specified class names and converts them
 * from div elements to proper anchor (<a>) elements while preserving content and attributes.
 */

import onAppearAnimations from "@/lib/helpers/animations";

/**
 * Interface for the result of anchor replacement operation
 */
interface AnchorReplacementResult {
  success: boolean;
  replacedCount: number;
  errors: string[];
  processedClasses: string[];
}

/**
 * Replace div elements with specified class names with anchor tags
 * This utility is designed to handle WordPress Gutenberg blocks that need to be
 * converted to proper anchor elements for navigation purposes.
 *
 * @param targetClasses - The class names of elements to replace (can be string or array of strings)
 * @param fallbackHref - Fallback href attribute if no child anchor is found (optional)
 * @returns Result object containing success status, count of replaced elements, and any errors
 */
export const replaceGutenbergAnchors = (
  targetClasses: string | string[] = "hover-block",
  fallbackHref?: string
): AnchorReplacementResult => {
  const result: AnchorReplacementResult = {
    success: false,
    replacedCount: 0,
    errors: [],
    processedClasses: [],
  };

  // Check if we're in a browser environment
  if (typeof document === "undefined") {
    result.errors.push("Function can only be used in browser environment");
    return result;
  }

  // Normalize targetClasses to array
  const classesToProcess = Array.isArray(targetClasses)
    ? targetClasses
    : [targetClasses];

  // Track whether any replaced node still carries appear-animation classes,
  // so we can re-attach the IntersectionObserver to the freshly created anchors.
  let hasAnimatedReplacement = false;

  try {
    // Process each target class
    classesToProcess.forEach((targetClass) => {
      try {
        // Find all elements with the specified class
        const targetElements = document.querySelectorAll(`.${targetClass}`);

        if (targetElements.length === 0) {
          result.errors.push(`No elements found with class "${targetClass}"`);
          return;
        }

        result.processedClasses.push(targetClass);

        // Process each target element
        targetElements.forEach((element, index) => {
          try {
            // Skip if element is already an anchor
            if (element.tagName.toLowerCase() === "a") {
              result.errors.push(
                `Element with class "${targetClass}" (${index + 1}) is already an anchor tag`
              );
              return;
            }

            // Look for anchor tags inside the element and extract href
            const childAnchors = element.querySelectorAll("a[href]");
            let targetHref = fallbackHref || `#${element.id || targetClass}`;

            if (childAnchors.length > 0) {
              // Use the first anchor's href
              const firstAnchor = childAnchors[0] as HTMLAnchorElement;
              targetHref = firstAnchor.href;
            }

            // Create new anchor element
            const anchorElement = document.createElement("a");

            // Copy all attributes from the original element
            Array.from(element.attributes).forEach((attr) => {
              anchorElement.setAttribute(attr.name, attr.value);
            });

            // Set href attribute
            anchorElement.setAttribute("href", targetHref);

            // Copy inner HTML (preserve all content)
            anchorElement.innerHTML = element.innerHTML;

            // Copy computed styles if needed (optional)
            const computedStyle = window.getComputedStyle(element);
            const importantStyles = [
              "display",
              "position",
              "top",
              "left",
              "right",
              "bottom",
              "width",
              "height",
              "margin",
              "padding",
              "border",
              "background",
              "color",
              "font-family",
              "font-size",
              "font-weight",
              "text-align",
              "line-height",
              "z-index",
            ];

            importantStyles.forEach((styleProp) => {
              const styleValue = computedStyle.getPropertyValue(styleProp);
              if (
                styleValue &&
                styleValue !== "auto" &&
                styleValue !== "normal"
              ) {
                anchorElement.style.setProperty(styleProp, styleValue);
              }
            });

            // Replace the element in the DOM
            if (element.parentNode) {
              element.parentNode.replaceChild(anchorElement, element);
              result.replacedCount++;

              // The new anchor is a fresh DOM node, so the appear-animation
              // IntersectionObserver attached to the original element no longer
              // applies. If it still carries `.elements_animated`, it would stay
              // stuck at opacity:0 (e.g. `.zoom-in` -> scale(0.8)) and never
              // become visible. Flag it so we can re-run the observer setup.
              if (anchorElement.classList.contains("elements_animated")) {
                hasAnimatedReplacement = true;
              }
            } else {
              result.errors.push(
                `Element with class "${targetClass}" (${index + 1}) has no parent node`
              );
            }
          } catch (error) {
            result.errors.push(
              `Error processing element with class "${targetClass}" (${index + 1}): ${error instanceof Error ? error.message : "Unknown error"}`
            );
          }
        });
      } catch (error) {
        result.errors.push(
          `Error processing class "${targetClass}": ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    });

    result.success = result.replacedCount > 0;

    // Re-attach the appear-animation observer to the newly created anchors so
    // any element with `.elements_animated` gets `.in-viewport` and becomes
    // visible, instead of staying hidden at opacity:0.
    if (hasAnimatedReplacement) {
      onAppearAnimations();
    }
  } catch (error) {
    result.errors.push(
      `General error: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }

  return result;
};

/**
 * Initialize anchor replacement on DOM content loaded
 * This function automatically runs the anchor replacement when the DOM is ready
 *
 * @param targetClasses - The class names of elements to replace (can be string or array of strings)
 * @param fallbackHref - Fallback href attribute if no child anchor is found (optional)
 * @param options - Additional options for the replacement
 */
export const initializeAnchorReplacement = (
  targetClasses: string | string[] = "hover-block",
  fallbackHref?: string,
  options: { autoRun?: boolean; delay?: number } = {}
): void => {
  const { autoRun = true, delay = 100 } = options;

  if (!autoRun) {
    return;
  }

  const runReplacement = () => {
    setTimeout(() => {
      const result = replaceGutenbergAnchors(targetClasses, fallbackHref);

      if (process.env.NODE_ENV === "development") {
        console.log("Anchor replacement result:", result);
      }
    }, delay);
  };

  // Run immediately if DOM is already loaded
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", runReplacement);
  } else {
    runReplacement();
  }
};

/**
 * Observer-based anchor replacement
 * Uses MutationObserver to watch for dynamic content and replace anchors as needed
 *
 * @param targetClasses - The class names of elements to replace (can be string or array of strings)
 * @param fallbackHref - Fallback href attribute if no child anchor is found (optional)
 * @returns Function to stop observing
 */
export const observeAndReplaceAnchors = (
  targetClasses: string | string[] = "hover-block",
  fallbackHref?: string
): (() => void) => {
  if (typeof MutationObserver === "undefined") {
    console.warn("MutationObserver not supported");
    return () => {};
  }

  // Normalize targetClasses to array for checking
  const classesToWatch = Array.isArray(targetClasses)
    ? targetClasses
    : [targetClasses];

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;

          // Check if the added node has any of the target classes
          const hasTargetClass = classesToWatch.some((className) =>
            element.classList.contains(className)
          );

          if (hasTargetClass) {
            replaceGutenbergAnchors(targetClasses, fallbackHref);
          }

          // Check if any child elements have any of the target classes
          const hasTargetElements = classesToWatch.some(
            (className) => element.querySelectorAll(`.${className}`).length > 0
          );

          if (hasTargetElements) {
            replaceGutenbergAnchors(targetClasses, fallbackHref);
          }
        }
      });
    });
  });

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Return function to stop observing
  return () => observer.disconnect();
};

export default {
  replaceGutenbergAnchors,
  initializeAnchorReplacement,
  observeAndReplaceAnchors,
};
